if (!customElements.get('gift-wrap-option')) {
  customElements.define(
    'gift-wrap-option',
    class GiftWrapOption extends HTMLElement {
      connectedCallback() {
        this.checkbox = this.querySelector('[data-gift-wrap-checkbox]');
        this.panel = this.querySelector('[data-gift-wrap-panel]');
        this.messageInput = this.querySelector('[data-gift-wrap-message]');
        this.counter = this.querySelector('[data-gift-wrap-count]');
        this.note = this.querySelector('[data-gift-wrap-note]');

        if (this.messageInput && this.dataset.existingMessage) {
          this.messageInput.value = this.dataset.existingMessage;
        }

        if (this.checkbox) this.checkbox.addEventListener('change', this.onToggle.bind(this));
        if (this.messageInput) this.messageInput.addEventListener('input', this.updateCounter.bind(this));

        this.onToggle();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshFromCart());
        }
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }

      get checked() {
        return !!(this.checkbox && this.checkbox.checked);
      }

      get variantId() {
        return parseInt(this.dataset.variantId, 10);
      }

      get perItem() {
        return this.dataset.mode === 'item';
      }

      get inCartQuantity() {
        return parseInt(this.dataset.inCart, 10) || 0;
      }

      get message() {
        return this.messageInput ? this.messageInput.value.trim() : '';
      }

      onToggle() {
        this.classList.toggle('is-checked', this.checked);
        if (this.panel) this.panel.hidden = !this.checked;

        // "Buy it now" posts straight to an express checkout and never touches the
        // cart, so it cannot carry the wrap. Hide it while the wrap is selected.
        const section = this.closest('.shopify-section');
        if (section && this.dataset.hideExpress === 'true') {
          section.classList.toggle('gift-wrap-selected', this.checked);
        }

        this.updateCounter();
      }

      updateCounter() {
        if (this.counter && this.messageInput) this.counter.textContent = this.messageInput.value.length;
      }

      // Line item properties for the gift wrap line. The leading underscore keeps
      // the flag out of the customer-facing cart and order summary.
      properties() {
        const properties = { _gift_wrap: 'true' };
        const message = this.message;
        if (message) properties[this.dataset.messageLabel || 'Gift message'] = message;
        return properties;
      }

      // "One per order" tops the cart up to a single wrap; "one per item" adds one
      // alongside every unit being added right now.
      quantityToAdd(mainQuantity) {
        if (this.perItem) return Math.max(1, parseInt(mainQuantity, 10) || 1);
        return this.inCartQuantity > 0 ? 0 : 1;
      }

      refreshFromCart() {
        return fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => response.json())
          .then((cart) => {
            this.syncFromCart(cart);
            return cart;
          })
          .catch(() => null);
      }

      // Mirrors the live cart into the block and returns the gift wrap line, if any.
      syncFromCart(cart) {
        if (!cart || !cart.items) return null;
        const line = cart.items.find((item) => item.variant_id === this.variantId) || null;

        this.dataset.inCart = line ? line.quantity : 0;
        if (this.note) this.note.hidden = !line;
        if (line && this.checkbox && !this.checkbox.checked) {
          this.checkbox.checked = true;
          this.onToggle();
        }
        return line;
      }
    }
  );
}

// The gift wrap cap moves with the cart: removing products can leave more wraps
// than there are items to wrap. Every cart render marks that line for a clamp,
// and this brings it back down (to 0, which removes it, when nothing is left).
(function () {
  function clampGiftWrapLine() {
    const row = document.querySelector('.cart-item[data-gift-wrap-clamp]');
    if (!row) return;

    const cap = parseInt(row.dataset.giftWrapClamp, 10);
    const line = parseInt(row.dataset.line, 10);
    const cartItems = row.closest('cart-drawer-items, cart-items');
    if (Number.isNaN(cap) || Number.isNaN(line)) return;
    if (!cartItems || typeof cartItems.updateQuantity !== 'function') return;

    // Drop the marker first: updateQuantity re-renders, and the fresh markup
    // decides on its own whether another clamp is still needed.
    row.removeAttribute('data-gift-wrap-clamp');
    cartItems.updateQuantity(line, cap, { currentTarget: null });
  }

  document.addEventListener('DOMContentLoaded', clampGiftWrapLine);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => setTimeout(clampGiftWrapLine, 0));
  }
})();


if (!customElements.get('gift-wrap-note')) {
  customElements.define(
    'gift-wrap-note',
    class GiftWrapNote extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('[data-gift-wrap-note-input]');
        this.errorEl = this.querySelector('[data-gift-wrap-note-error]');
        if (this.errorEl && !this.errorEl.dataset.defaultMessage) {
          this.errorEl.dataset.defaultMessage = this.errorEl.textContent.trim();
        }
        if (!this.input) return;

        this.savedValue = this.value;

        this.input.addEventListener('input', () => {
          if (this.valid) this.hideError();
          clearTimeout(this.saveTimer);
          this.saveTimer = setTimeout(() => this.save(), 700);
        });
        this.input.addEventListener('blur', () => this.save());
      }

      disconnectedCallback() {
        clearTimeout(this.saveTimer);
      }

      get required() {
        return this.dataset.required === 'true';
      }

      get value() {
        return this.input ? this.input.value.trim() : '';
      }

      get valid() {
        return !this.required || this.value.length > 0;
      }

      get dirty() {
        return this.value !== this.savedValue;
      }

      showError() {
        this.classList.add('has-error');
        if (this.errorEl) this.errorEl.hidden = false;
      }

      hideError() {
        this.classList.remove('has-error');
        if (this.errorEl) this.errorEl.hidden = true;
      }

      focusInput() {
        if (!this.input) return;
        this.input.scrollIntoView({ block: 'center', behavior: 'smooth' });
        this.input.focus();
      }

      // Liquid serialises line item properties as an array of [key, value] pairs,
      // not an object, so normalise before merging.
      currentProperties() {
        let parsed;
        try {
          parsed = JSON.parse(this.dataset.properties || '{}');
        } catch (e) {
          return {};
        }

        if (Array.isArray(parsed)) {
          return parsed.reduce((all, pair) => {
            if (Array.isArray(pair) && pair.length >= 2) all[pair[0]] = pair[1];
            return all;
          }, {});
        }

        return parsed && typeof parsed === 'object' ? parsed : {};
      }

      // Writes the note onto the gift wrap line. /cart/change.js replaces the whole
      // property set, so the existing ones are merged back in rather than dropped.
      save() {
        clearTimeout(this.saveTimer);
        if (!this.dirty || this.saving) return Promise.resolve();

        const properties = this.currentProperties();
        const label = this.dataset.label || 'Wrap instructions';
        const value = this.value;
        if (value) {
          properties[label] = value;
        } else {
          delete properties[label];
        }

        this.saving = true;
        this.classList.add('is-saving');

        return fetch(`${routes.cart_change_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: this.dataset.key, properties: properties }),
        })
          .then((response) =>
            response.json().then((cart) => {
              // fetch does not reject on 4xx, so check explicitly rather than
              // reporting a failed save as a successful one.
              if (!response.ok) throw new Error(cart.description || cart.message || 'Cart update failed');
              return cart;
            })
          )
          .then((cart) => {
            this.savedValue = value;
            this.dataset.properties = JSON.stringify(properties);
            // Changing properties gives the line a new key; keep ours in step.
            const line = (cart.items || []).find(
              (cartItem) => cartItem.properties && cartItem.properties._gift_wrap
            );
            if (line) this.dataset.key = line.key;
            this.hideSaveError();
          })
          .catch(() => {
            this.showSaveError();
          })
          .finally(() => {
            this.saving = false;
            this.classList.remove('is-saving');
          });
      }

      showSaveError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = "We couldn't save your wrapping note. Please try again.";
        this.errorEl.hidden = false;
        this.classList.add('has-error');
      }

      hideSaveError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = this.errorEl.dataset.defaultMessage || this.errorEl.textContent;
        if (this.valid) {
          this.errorEl.hidden = true;
          this.classList.remove('has-error');
        }
      }
    }
  );
}

// Checkout gate: when the note is required and empty, stop the customer leaving
// the cart with wrapping we cannot fulfil. Capture phase so this runs before the
// theme's own submit handlers.
(function () {
  // Covers the theme's own buttons and the Shiprocket headless checkout button,
  // which replaces them on this store.
  const CHECKOUT_SELECTOR = [
    '[name="checkout"]',
    '.cart__ctas',
    '.cart__checkout-button',
    '.cart__dynamic-checkout-buttons',
    '.additional-checkout-buttons',
    '.shopify-payment-button',
    '[name="sr-headless-button"]',
    '.sr-headless-checkout',
  ].join(', ');

  function invalidNotes() {
    return Array.from(document.querySelectorAll('gift-wrap-note')).filter((note) => !note.valid);
  }

  function pendingNotes() {
    return Array.from(document.querySelectorAll('gift-wrap-note')).filter((note) => note.dirty);
  }

  function block(event) {
    const invalid = invalidNotes();
    if (invalid.length) {
      event.preventDefault();
      event.stopPropagation();
      invalid.forEach((note) => note.showError());
      invalid[0].focusInput();
      return true;
    }
    return false;
  }

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target.closest(CHECKOUT_SELECTOR) : null;
      if (!target) return;

      if (block(event)) return;

      // Valid but not written yet: hold the click, persist, then let it through.
      const pending = pendingNotes();
      if (pending.length && !target.dataset.giftWrapResubmit) {
        event.preventDefault();
        event.stopPropagation();
        Promise.all(pending.map((note) => note.save())).then(() => {
          target.dataset.giftWrapResubmit = 'true';
          target.click();
          delete target.dataset.giftWrapResubmit;
        });
      }
    },
    true
  );

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof Element)) return;
    if (!form.querySelector('[name="checkout"]') && form.id !== 'cart' && form.id !== 'CartDrawer-Form') return;
    block(event);
  }, true);
})();
